import React from 'react';
import ReactDOM from 'react-dom/client';
import { ConfigProvider, theme, App as AntdApp } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
// —— PostHog & Sentry 初始化 ——
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';
import './i18n';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import App from './App';
import { registerPromptHandler } from './utils/auth-guard';
import './styles/global.css';

// Sentry 初始化（仅在生产环境启用）
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: import.meta.env.PROD ? 0.05 : 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// PostHog 初始化
const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    capture_pageview: true,
    capture_pageleave: true,
    persistence: 'localStorage',
  });
}

function I18nConfigProvider({ children }) {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'en-US' ? enUS : zhCN;

  React.useEffect(() => {
    dayjs.locale(i18n.language === 'en-US' ? 'en' : 'zh-cn');
  }, [i18n.language]);

  return (
    <ConfigProvider
      locale={locale}
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: '#F4DFA2',
          colorInfo: '#9BB0CF',
          colorSuccess: '#69B89A',
          colorWarning: '#F4DFA2',
          colorError: '#D98282',
          colorBgContainer: '#07152D',
          colorBgElevated: '#040D1E',
          colorBgSpotlight: '#17365E',
          colorBgMask: 'rgba(7, 21, 45, 0.92)',
          colorBorder: 'rgba(255,255,255,0.08)',
          colorBorderSecondary: 'rgba(255,255,255,0.12)',
          colorText: '#E9F2FF',
          colorTextSecondary: '#9BB0CF',
          colorTextTertiary: '#647A9C',
          colorTextQuaternary: '#3A4D6B',
          borderRadius: 18,
          borderRadiusLG: 24,
          borderRadiusSM: 12,
          borderRadiusXS: 6,
          wireframe: false,
          fontFamily: '"Inter","Noto Sans SC",-apple-system,BlinkMacSystemFont,"SF Pro Display","HarmonyOS Sans SC","PingFang SC",sans-serif',
          fontSizeHeading1: 48,
          fontSizeHeading2: 32,
          fontSizeHeading3: 24,
          fontSizeHeading4: 18,
          fontSizeLG: 18,
          lineHeight: 1.6,
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          boxShadowSecondary: '0 20px 60px rgba(0,0,0,0.18)',
        },
        components: {
          Layout: {
            siderBg: 'rgba(7, 21, 45, 0.95)',
            headerBg: 'rgba(7, 21, 45, 0.92)',
            bodyBg: '#07152D',
          },
          Card: {
            colorBgContainer: 'rgba(255,255,255,0.035)',
            colorBorderSecondary: 'rgba(255,255,255,0.08)',
          },
          Button: {
            primaryShadow: '0 2px 10px rgba(244,223,162,0.15)',
            defaultShadow: '0 2px 8px rgba(0,0,0,0.25)',
            dangerShadow: '0 4px 14px rgba(217,130,130,0.15)',
            borderRadius: 18,
            colorPrimary: '#F4DFA2',
            colorPrimaryText: '#08162B',
          },
          Input: {
            colorBgContainer: 'rgba(255,255,255,0.03)',
            activeBorderColor: '#F4DFA2',
            hoverBorderColor: 'rgba(244,223,162,0.4)',
            borderRadius: 12,
          },
          Select: {
            colorBgContainer: 'rgba(255,255,255,0.03)',
            optionSelectedBg: 'rgba(244,223,162,0.08)',
          },
          Modal: {
            colorBgElevated: '#040D1E',
            contentBg: '#040D1E',
            headerBg: '#040D1E',
          },
          Menu: {
            darkItemBg: 'transparent',
            darkSubMenuItemBg: 'transparent',
            darkItemSelectedBg: 'rgba(244,223,162,0.08)',
            darkItemHoverBg: 'rgba(255,255,255,0.03)',
            darkItemSelectedColor: '#F4DFA2',
          },
          Tag: {
            colorFillQuaternary: 'rgba(255,255,255,0.03)',
            colorFillSecondary: 'rgba(255,255,255,0.06)',
            colorFillTertiary: 'rgba(255,255,255,0.04)',
            borderRadiusSM: 6,
          },
          Tabs: {
            colorPrimary: '#F4DFA2',
            inkBarColor: '#F4DFA2',
          },
          Dropdown: {
            colorBgElevated: '#040D1E',
            controlItemBgHover: 'rgba(244,223,162,0.06)',
            boxShadowSecondary: '0 8px 32px rgba(0,0,0,0.3)',
          },
          Tooltip: { colorBgSpotlight: '#040D1E' },
          Descriptions: { colorFillAlter: 'rgba(255,255,255,0.02)' },
          Badge: { indicatorHeightSM: 16, indicatorHeight: 18 },
          Empty: { colorTextDisabled: 'rgba(255,255,255,0.25)' },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
}

/** 把 antd 动态 message 注入给请求层，用于未登录提示 */
function MessageBridge() {
  const { message } = AntdApp.useApp();
  React.useEffect(() => {
    registerPromptHandler((text) => message.warning(text));
    return () => registerPromptHandler(null);
  }, [message]);
  return null;
}

function Root() {
  return (
    <React.StrictMode>
      <I18nConfigProvider>
        <AntdApp>
          <MessageBridge />
          <App />
        </AntdApp>
      </I18nConfigProvider>
    </React.StrictMode>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<Root />);
